import React from 'react';
import { OperationProgressWindow } from './OperationProgress';
import type { OperationProgressWindowRequestInput } from './operationProgressWindowRequestStore';
import { operationProgressWindowRequestStore } from './operationProgressWindowRequestStoreInstance';
import { diagLog } from '../../api/grpc/webLogger';
import { isWebView2Runtime } from '../../api/grpc/grpcRuntime';
import { isNativeOwnedOperationProgress } from './operationProgressUtils';

export type OperationProgressWindowRequestProps = OperationProgressWindowRequestInput;

export const OperationProgressWindowRequest: React.FC<OperationProgressWindowRequestProps> = ({
  id,
  open,
  model,
  priority = 0,
  onClose,
  maxWidth,
  suppressInWebView2 = false,
}) => {
  React.useEffect(() => {
    const nativeOwned = isNativeOwnedOperationProgress(model.operationType);
    if ((suppressInWebView2 || nativeOwned) && isWebView2Runtime()) {
      operationProgressWindowRequestStore.remove(id);
      return;
    }

    diagLog('progress-watchdog', `store:upsert:start id=${id} open=${open}`);
    operationProgressWindowRequestStore.upsert({
      id,
      open,
      model,
      priority,
      onClose,
      maxWidth,
    });
    diagLog('progress-watchdog', `store:upsert:end id=${id}`);
  }, [id, maxWidth, model, onClose, open, priority, suppressInWebView2]);

  React.useEffect(() => {
    return () => {
      operationProgressWindowRequestStore.remove(id);
    };
  }, [id]);

  return null;
};

export const OperationProgressWindowHost: React.FC = () => {
  const request = React.useSyncExternalStore(
    operationProgressWindowRequestStore.subscribe,
    operationProgressWindowRequestStore.getSnapshot,
    operationProgressWindowRequestStore.getSnapshot
  );

  if (!request) {
    return null;
  }

  const handleClose = () => {
    request.onClose?.();
    operationProgressWindowRequestStore.remove(request.id);
  };

  return (
    <OperationProgressWindow
      open
      model={request.model}
      maxWidth={request.maxWidth}
      onClose={handleClose}
    />
  );
};
